import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { useI18n } from '../contexts/I18nContext';
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

export function ResetPasswordPage() {
  const { tt } = useI18n();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { updatePassword, session, isPasswordRecovery, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = tt('Reset your password') + ' - Llambada';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = 'Llambada';
      document.head.removeChild(meta);
    };
  }, [tt]);

  // Also check URL hash directly as a fallback (in case the auth context check runs before hash is parsed)
  const [hashHasRecovery, setHashHasRecovery] = useState(false);

  useEffect(() => {
    // Check hash immediately
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setHashHasRecovery(true);
    }
  }, []);

  // Determine if token is valid - need a session AND (isPasswordRecovery OR hash indicates recovery)
  const validToken = session && (isPasswordRecovery || hashHasRecovery);
  
  // Still loading if auth is loading, or if we have a hash that might indicate recovery but no session yet
  const stillChecking = authLoading || (hashHasRecovery && !session);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(tt('Password must be at least 6 characters'));
      return;
    }

    if (password !== confirmPassword) {
      setError(tt('Passwords do not match'));
      return;
    }

    setLoading(true);

    const { error } = await updatePassword(password);

    setLoading(false);

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col items-center justify-center p-4">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <img 
            src="/logo.png" 
            alt="Llambada" 
            className="h-16 w-16 mx-auto rounded-2xl shadow-xl shadow-accent-500/25 mb-6"
          />
          <h1 className="font-display text-3xl font-bold text-theme-text mb-2">
            {tt('Set new password')}
          </h1>
          <p className="text-theme-text-secondary">
            {tt('Choose a new password for your account')}
          </p>
        </div>

        <div className="card p-8 animate-slide-up">
          {stillChecking ? (
            <div className="text-center py-8">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-theme-text-secondary">{tt('Validating reset link...')}</p>
            </div>
          ) : !validToken ? (
            <div className="text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-theme-error-bg mb-6">
                <AlertCircle className="h-8 w-8 text-theme-error" />
              </div>
              <h2 className="font-display text-xl font-semibold text-theme-text mb-2">
                {tt('Invalid or expired link')}
              </h2>
              <p className="text-theme-text-secondary mb-6">
                {tt('This password reset link is invalid or has expired. Please request a new one.')}
              </p>
              <Link to="/forgot-password" className="btn btn-primary w-full">
                {tt('Request new link')}
              </Link>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-theme-success-bg mb-6">
                <CheckCircle className="h-8 w-8 text-theme-success" />
              </div>
              <h2 className="font-display text-xl font-semibold text-theme-text mb-2">
                {tt('Password updated!')}
              </h2>
              <p className="text-theme-text-secondary mb-6">
                {tt('Your password has been successfully changed. Redirecting you to your dashboard...')}
              </p>
              <LoadingSpinner size="md" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-theme-error-bg border border-theme-error-border text-theme-error">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <p className="text-sm">{tt(error)}</p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-theme-text-secondary mb-2">
                  {tt('New password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-11 pr-11"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-text-muted hover:text-theme-text transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-theme-text-muted">
                  {tt('Must be at least 6 characters')}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-theme-text-secondary mb-2">
                  {tt('Confirm new password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pl-11 pr-11"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-text-muted hover:text-theme-text transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full py-3"
              >
                  {loading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    tt('Update password')
                  )}
                </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
